import React, { useState, useEffect } from 'react';
import { driverAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Users, Copy, Gift, CheckCircle, Clock, Share2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DriverReferral = () => {
  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState(null);

  useEffect(() => {
    loadReferralData();
  }, []);

  const loadReferralData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/driver/referral`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('reddi_token')}`
        }
      });
      const data = await response.json();
      setReferralData(data);
    } catch (error) {
      console.error('Error loading referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralData?.referral_code || '');
    toast.success('Referral code copied!');
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralData?.referral_link || '');
    toast.success('Referral link copied!');
  };

  const shareReferral = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Reddi as a Driver!',
          text: `Use my referral code ${referralData?.referral_code} to sign up and we both get a bonus!`,
          url: referralData?.referral_link
        });
      } catch (error) {
        copyReferralLink();
      }
    } else {
      copyReferralLink();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="driver-referral-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Refer Drivers
        </h1>

        {/* Referral Code Card */}
        <Card className="mb-6 bg-gradient-to-br from-red-500 to-red-600 border-0 text-white">
          <CardContent className="p-8">
            <div className="text-center">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-80" />
              <h2 className="text-xl font-semibold mb-2">Your Referral Code</h2>
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="text-4xl font-display font-bold tracking-wider">
                  {referralData?.referral_code}
                </span>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={copyReferralCode}
                  className="text-white hover:bg-white/20"
                >
                  <Copy className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-white/80 mb-6">
                Share this code with other drivers and earn {referralData?.bonus_percent}% bonus on your fares for {referralData?.bonus_days} days!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={copyReferralLink}
                  className="bg-white text-red-600 hover:bg-white/90 font-semibold"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  onClick={shareReferral}
                  variant="outline"
                  className="border-white text-white hover:bg-white/20"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card className="mb-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight">How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-red-600">1</span>
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Share Your Code</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Send your referral code to friends who want to drive
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-red-600">2</span>
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">They Complete {referralData?.min_rides_required} Rides</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Your referral needs to complete {referralData?.min_rides_required} rides to activate the bonus
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Gift className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">You Earn {referralData?.bonus_percent}% Bonus</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Get a {referralData?.bonus_percent}% bonus on all your fares for {referralData?.bonus_days} days
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6 text-center">
              <p className="text-3xl font-display font-bold text-slate-900 dark:text-white">
                {referralData?.total_referrals || 0}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Total Referrals</p>
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6 text-center">
              <p className="text-3xl font-display font-bold text-green-600">
                {referralData?.successful_referrals || 0}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Successful</p>
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-6 text-center">
              <p className="text-3xl font-display font-bold text-amber-600">
                {referralData?.pending_referrals || 0}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Pending</p>
            </CardContent>
          </Card>
        </div>

        {/* Active Bonuses */}
        {referralData?.active_bonuses?.length > 0 && (
          <Card className="mb-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                <Gift className="w-5 h-5 text-green-600" />
                Active Bonuses
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {referralData.active_bonuses.map((bonus, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
                    <div>
                      <p className="font-semibold text-green-700 dark:text-green-400">
                        +{bonus.bonus_percent * 100}% Referral Bonus
                      </p>
                      <p className="text-sm text-green-600 dark:text-green-500">
                        Expires: {new Date(bonus.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400">
                      Active
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Referral History */}
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight">Your Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            {referralData?.referrals?.length > 0 ? (
              <div className="space-y-3">
                {referralData.referrals.map((referral, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-200 dark:bg-slate-600 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{referral.referred_name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {referral.rides_completed}/{referralData.min_rides_required} rides
                        </p>
                      </div>
                    </div>
                    <Badge className={
                      referral.status === 'activated' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-400'
                    }>
                      {referral.status === 'activated' ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> Activated</>
                      ) : (
                        <><Clock className="w-3 h-3 mr-1" /> Pending</>
                      )}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 dark:text-slate-400 py-8">
                No referrals yet. Share your code to get started!
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DriverReferral;
